import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Select,
  TextInput,
} from "flowbite-react";
import type { FC } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HiHome, HiSearch } from "react-icons/hi";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import { Pagination } from "../../pages/users/list";
import type {
  ContractType,
  ContractTypeStatusFilter,
} from "../../services/contract-types";
import { getContractTypesPage } from "../../services/contract-types";
import type { Commission } from "../../services/commissions";
import { getCommissionsByContractType } from "../../services/commissions";
import type { ContractCompany } from "../../services/contract-companies";
import { getContractCompanies } from "../../services/contract-companies";
import type { ContractCategory } from "../../services/contract-categories";
import { getContractCategories } from "../../services/contract-categories";
import { MultiSelect } from "../../components/MultiSelect";
import CommissionsModal from "../../components/contracts/CommissionsModal";
import CategoriesModal from "../../components/contracts/CategoriesModal";
import CompaniesModal from "../../components/contracts/CompaniesModal";
import { ContractTypesTable } from "./ContractTypesTable";
import { AddContractTypeModal } from "./AddContractTypeModal";
import { CommissionLevelsModal } from "../../components/contracts/CommissionLevelsModal";

const ContractTypesPage: FC = function () {
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [contractTypes, setContractTypes] = useState<ContractType[]>([]);
  const [showCategories, setShowCategories] = useState(false);
  const [showCompanies, setShowCompanies] = useState(false);
  const [showCommissionLevels, setShowCommissionLevels] = useState(false);
  const [selectedContractType, setSelectedContractType] = useState<{
    id: number;
    commissions: Commission[];
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [companies, setCompanies] = useState<ContractCompany[]>([]);
  const [categories, setCategories] = useState<ContractCategory[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] =
    useState<ContractTypeStatusFilter>("all");

  const itemsPerPage = 20;
  const requestIdRef = useRef(0);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  const fetchCompanies = useCallback(async () => {
    try {
      const companiesData = await getContractCompanies();
      setCompanies(companiesData || []);
    } catch (error) {
      console.error("Error fetching companies:", error);
      setCompanies([]);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const categoriesData = await getContractCategories();
      setCategories(categoriesData || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    }
  }, []);

  const fetchContractTypesPageData = useCallback(
    async (page: number) => {
      const requestId = ++requestIdRef.current;

      try {
        const { data, total } = await getContractTypesPage({
          page,
          pageSize: itemsPerPage,
          searchTerm: debouncedSearchTerm,
          companies: selectedCompanies,
          categories: selectedCategories,
          status: selectedStatus,
        });

        if (requestId !== requestIdRef.current) {
          return;
        }

        setTotalItems(total);

        const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
        if (page > totalPages) {
          setContractTypes([]);
          setCurrentPage(totalPages);
          return;
        }

        setContractTypes(data || []);
      } catch (error) {
        if (requestId !== requestIdRef.current) {
          return;
        }

        console.error("Error fetching contract types page:", error);
        setContractTypes([]);
        setTotalItems(0);
      }
    },
    [
      debouncedSearchTerm,
      itemsPerPage,
      selectedCategories,
      selectedCompanies,
      selectedStatus,
    ]
  );

  const refreshCurrentPage = useCallback(async () => {
    await fetchContractTypesPageData(currentPage);
  }, [currentPage, fetchContractTypesPageData]);

  const refreshCurrentPageAndReferenceData = useCallback(async () => {
    await Promise.all([
      fetchContractTypesPageData(currentPage),
      fetchCompanies(),
      fetchCategories(),
    ]);
  }, [
    currentPage,
    fetchCategories,
    fetchCompanies,
    fetchContractTypesPageData,
  ]);

  useEffect(() => {
    fetchCompanies();
    fetchCategories();
  }, [fetchCategories, fetchCompanies]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearchTerm,
    selectedCompanies,
    selectedCategories,
    selectedStatus,
  ]);

  useEffect(() => {
    fetchContractTypesPageData(currentPage);
  }, [currentPage, fetchContractTypesPageData]);

  const handleOpenCommissions = async (contractType: ContractType) => {
    try {
      const commissions = await getCommissionsByContractType(contractType.id);
      setSelectedContractType({
        id: contractType.id,
        commissions: commissions || [],
      });
    } catch (error) {
      console.error("Error fetching commissions:", error);
      setSelectedContractType({
        id: contractType.id,
        commissions: [],
      });
    }
  };

  const handleCloseCommissions = () => {
    setSelectedContractType(null);
  };

  const companyOptions = useMemo(() => {
    if (!companies || !Array.isArray(companies)) {
      return [];
    }

    try {
      return companies.map((company) => ({
        value: company.company,
        label: company.company,
      }));
    } catch (error) {
      console.error("Error creating company options:", error);
      return [];
    }
  }, [companies]);

  const categoryOptions = useMemo(() => {
    if (!categories || !Array.isArray(categories)) {
      return [];
    }

    try {
      return categories.map((category) => ({
        value: category.category,
        label: category.category,
      }));
    } catch (error) {
      console.error("Error creating category options:", error);
      return [];
    }
  }, [categories]);

  return (
    <NavbarSidebarLayout isFooter={false} pageTitle="Vertragstypen">
      <div className="block items-center justify-between border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:flex">
        <div className="mb-1 w-full">
          <div className="mb-4">
            <Breadcrumb className="mb-4">
              <BreadcrumbItem href="#">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">Startseite</span>
                </div>
              </BreadcrumbItem>
              <BreadcrumbItem href="/contract-types">
                Vertragstypen
              </BreadcrumbItem>
            </Breadcrumb>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
              Alle Vertragstypen
            </h1>
          </div>
          <div className="flex items-center justify-end gap-x-3">
            <Button color="primary" onClick={() => setShowCategories(true)}>
              Kategorien verwalten
            </Button>
            <Button color="primary" onClick={() => setShowCompanies(true)}>
              Unternehmen verwalten
            </Button>
            <Button
              color="primary"
              onClick={() => setShowCommissionLevels(true)}
            >
              Provisions-Level verwalten
            </Button>
            <AddContractTypeModal
              onUpdate={async () => {
                await refreshCurrentPageAndReferenceData();
              }}
            />
          </div>
        </div>
      </div>

      <div className="bg-white p-4 dark:bg-gray-800">
        <div className="mb-4">
          <TextInput
            id="search-contract-types"
            type="text"
            icon={HiSearch}
            placeholder="Suche nach Vertragstyp, Unternehmen, Kategorie..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="min-w-[250px] flex-1">
            <div className="mb-2 text-sm font-medium text-gray-900 dark:text-white">
              Unternehmen
            </div>
            <MultiSelect
              id="company-filter"
              label=""
              options={companyOptions}
              value={selectedCompanies}
              onChange={setSelectedCompanies}
              placeholder="Alle Unternehmen"
            />
          </div>
          <div className="min-w-[250px] flex-1">
            <div className="mb-2 text-sm font-medium text-gray-900 dark:text-white">
              Kategorien
            </div>
            <MultiSelect
              id="category-filter"
              label=""
              options={categoryOptions}
              value={selectedCategories}
              onChange={setSelectedCategories}
              placeholder="Alle Kategorien"
            />
          </div>
          <div className="min-w-[200px] flex-1">
            <div className="mb-2 text-sm font-medium text-gray-900 dark:text-white">
              Status
            </div>
            <Select
              id="status-filter"
              value={selectedStatus}
              onChange={(e) =>
                setSelectedStatus(e.target.value as ContractTypeStatusFilter)
              }
            >
              <option value="all">Alle</option>
              <option value="active">Aktiv</option>
              <option value="inactive">Inaktiv</option>
            </Select>
          </div>
        </div>
      </div>
      <div className="flex flex-col">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden shadow">
              <ContractTypesTable
                contractTypes={contractTypes}
                onUpdate={refreshCurrentPageAndReferenceData}
                onManageCommissions={handleOpenCommissions}
              />
            </div>
          </div>
        </div>
      </div>
      <Pagination
        totalUsers={totalItems}
        currentPage={currentPage}
        usersPerPage={itemsPerPage}
        setCurrentPage={(page) => {
          setCurrentPage(page);
          window.scrollTo(0, 0);
        }}
      />
      {selectedContractType && (
        <CommissionsModal
          show={true}
          onClose={handleCloseCommissions}
          contractTypeId={selectedContractType.id}
          commissions={selectedContractType.commissions}
          onUpdate={async () => {
            try {
              await refreshCurrentPage();

              if (selectedContractType) {
                const commissions = await getCommissionsByContractType(
                  selectedContractType.id
                );
                setSelectedContractType({
                  ...selectedContractType,
                  commissions: commissions || [],
                });
              }
            } catch (error) {
              console.error("Error updating commissions:", error);
            }
          }}
        />
      )}
      <CategoriesModal
        show={showCategories}
        onClose={() => setShowCategories(false)}
        onUpdate={refreshCurrentPageAndReferenceData}
      />
      <CompaniesModal
        show={showCompanies}
        onClose={() => setShowCompanies(false)}
        onUpdate={refreshCurrentPageAndReferenceData}
      />
      <CommissionLevelsModal
        show={showCommissionLevels}
        onClose={() => setShowCommissionLevels(false)}
      />
    </NavbarSidebarLayout>
  );
};

export default ContractTypesPage;
